/**
 * Options pane — the agent asking the USER for a decision before it acts.
 *
 * Standing on its own it takes the prompt editor's place, so it wears the
 * prompt's frame (`InputFrame`), named "input needed" in the top border, with
 * the step's key hints on the first row inside. Embedded in the `--team` attach
 * panel it draws no frame of its own: that panel is already a framed surface,
 * and a second frame inside it is two boxes one column apart.
 *
 * One decision per step:
 *   - tui.select.up / down    move between options (wraps)
 *   - app.options.next (→)    confirm the highlighted option and advance; on the
 *                             last step it submits every answer
 *   - app.options.back (←)    step back to the previous decision
 *   - 1-9                     quick-pick an option
 *   - drop onto the custom row and type a free-form answer when none fit; there
 *     the arrows move the text cursor first and only act on the step at its
 *     ends, so enter is the key that always commits
 *   - tui.select.cancel (esc) skips the whole sequence
 *
 * Answered steps stay on screen as a deterministic breadcrumb so you always
 * see what you've already committed to.
 */

import {
	type Component,
	DEFAULT_INPUT_PROMPT,
	type Focusable,
	getKeybindings,
	Input,
	truncateToWidth,
	visibleWidth,
} from "@kolisachint/hoocode-tui";
import type { AskQuestion } from "../../../core/extensions/types.js";
import { SELECT_CURSOR, SELECT_GUTTER, theme } from "../theme/theme.js";
import { InputFrame } from "./input-frame.js";

export interface AskOptionsOptions {
	/**
	 * Draw the prompt's frame around the pane. False for a pane embedded in a
	 * surface that already frames it (the team attach panel).
	 */
	framed?: boolean;
}

export class AskOptionsComponent implements Component, Focusable {
	private questions: AskQuestion[];
	private step = 0;
	private index = 0;
	private answers: string[] = [];
	private customInput = new Input();
	private onSubmitCallback: (answers: string[]) => void;
	private onCancelCallback: () => void;

	private _focused = false;
	get focused(): boolean {
		return this._focused;
	}
	set focused(value: boolean) {
		this._focused = value;
		this.customInput.focused = value;
	}

	/** Undefined when the pane is embedded and its host draws the frame. */
	private frame: InputFrame | undefined;

	constructor(
		questions: AskQuestion[],
		onSubmit: (answers: string[]) => void,
		onCancel: () => void,
		options: AskOptionsOptions = {},
	) {
		this.questions = questions;
		this.onSubmitCallback = onSubmit;
		this.onCancelCallback = onCancel;
		if (options.framed !== false) {
			this.frame = new InputFrame({ title: "input needed" });
			// The body is recomputed from live state on every frame, so it goes in
			// as an adapter rather than as a component holding its own lines.
			this.frame.addChild({ render: (width: number) => this.renderBody(width, false), invalidate: () => {} });
		}
	}

	invalidate(): void {
		this.frame?.invalidate();
	}

	private rowCount(q: AskQuestion): number {
		return q.options.length + (q.allowCustom ? 1 : 0);
	}

	private isOnCustomRow(q: AskQuestion): boolean {
		return !!q.allowCustom && this.index === q.options.length;
	}

	/**
	 * How the arrow keys divide between the step and the text on the custom row.
	 *
	 * The step arrows and the text cursor want the same two keys, and they are
	 * only ever in conflict on the free-text row with something typed in it.
	 * There the text gets first refusal and the step takes what is left over at
	 * the ends: `→` at the end of the answer commits it (with nothing typed the
	 * end is also the start, which is why the plain custom row still advances),
	 * and `←` never leaves the step while there is an answer to lose — stepping
	 * back clears the field, which is not what a cursor key should do.
	 */
	private customArrows(): { next: boolean; back: boolean } {
		if (!this.isOnCustomRow(this.questions[this.step])) return { next: true, back: true };
		const typed = this.customInput.getValue().length;
		return { next: this.customInput.getCursor() >= typed, back: typed === 0 };
	}

	private spread(left: string, right: string, width: number): string {
		const gap = Math.max(1, width - visibleWidth(left) - visibleWidth(right));
		return truncateToWidth(`${left}${" ".repeat(gap)}${right}`, width, "");
	}

	private renderHints(last: boolean): string {
		const kb = getKeybindings();
		const sep = theme.fg("muted", " · ");
		const hint = (keys: string, desc: string) => theme.fg("dim", keys) + theme.fg("muted", ` ${desc}`);
		const upDown = `${kb.getKeys("tui.select.up").join("/")}/${kb.getKeys("tui.select.down").join("/")}`;
		const arrows = this.customArrows();
		const parts = [hint(upDown, "move")];
		// Mid-answer on the free-text row the arrows are the text cursor's, so the
		// hint names the key that does commit it.
		const nextKeys = arrows.next ? kb.getKeys("app.options.next") : kb.getKeys("tui.select.confirm");
		parts.push(hint(nextKeys.join("/"), last ? "submit" : "next"));
		if (this.step > 0 && arrows.back) {
			parts.push(hint(kb.getKeys("app.options.back").join("/"), "back"));
		}
		parts.push(hint(kb.getKeys("tui.select.cancel").join("/"), "skip"));
		return parts.join(sep);
	}

	render(width: number): string[] {
		if (this.frame) return this.frame.render(width);
		return this.renderBody(width, true);
	}

	/**
	 * The pane's rows. `labelled` prints the "INPUT NEEDED" name on the hint
	 * row; a framed pane has that name in its border already.
	 */
	private renderBody(width: number, labelled: boolean): string[] {
		const lines: string[] = [];
		const cur = this.questions[this.step];
		const last = this.step === this.questions.length - 1;

		// Key hints for this step, flush right; the name on the left when the pane
		// is not carrying it in a border.
		const title = labelled ? theme.bold(theme.fg("borderAccent", "INPUT NEEDED")) : "";
		lines.push(this.spread(title, this.renderHints(last), width));

		// Answered-step breadcrumb.
		for (let i = 0; i < this.step; i++) {
			const q = this.questions[i];
			const check = theme.fg("success", "✓");
			const label = theme.fg("dim", q.short ?? q.question);
			const arrow = theme.fg("dim", "→ ");
			const answer = theme.fg("accent", this.answers[i] ?? "");
			lines.push(truncateToWidth(`${check} ${label}   ${arrow}${answer}`, width, "..."));
		}
		if (this.step > 0) lines.push("");

		// Current question.
		const stepLabel = theme.fg("dim", `${this.step + 1}/${this.questions.length}`);
		lines.push(truncateToWidth(`${stepLabel} ${theme.bold(cur.question)}`, width, "..."));
		if (cur.detail) lines.push(truncateToWidth(theme.fg("muted", `    ${cur.detail}`), width, "..."));
		lines.push("");

		// Options.
		for (let i = 0; i < cur.options.length; i++) {
			const o = cur.options[i];
			const active = i === this.index;
			const cursor = theme.fg("accent", active ? SELECT_CURSOR : SELECT_GUTTER);
			const num = theme.fg(active ? "accent" : "dim", String(i + 1));
			const label = active ? theme.bold(o.label) : o.label;
			let line = `${cursor}${num} ${label}`;
			if (o.recommended) line += theme.fg("success", " (recommended)");
			if (o.description) line += theme.fg("muted", `   ${o.description}`);
			lines.push(truncateToWidth(line, width, "..."));
		}

		// Custom row.
		if (cur.allowCustom) {
			const customIndex = cur.options.length;
			const active = this.index === customIndex;
			const cursor = theme.fg("accent", active ? SELECT_CURSOR : SELECT_GUTTER);
			const plus = theme.fg(active ? "accent" : "dim", "+");
			if (active) {
				const value = this.customInput.getValue();
				const prompt = theme.fg("muted", DEFAULT_INPUT_PROMPT);
				const caret = this._focused ? theme.fg("accent", "▏") : "";
				const body = value
					? `${theme.fg("text", value)}${caret}`
					: `${caret}${theme.fg("dim", "type your own answer")}`;
				lines.push(truncateToWidth(`${cursor}${plus} ${prompt} ${body}`, width, "..."));
			} else {
				const label = theme.fg("muted", "custom answer");
				const desc = theme.fg("dim", "type your own");
				lines.push(truncateToWidth(`${cursor}${plus} ${label}   ${desc}`, width, "..."));
			}
		}

		// Count.
		lines.push(theme.fg("dim", `(${this.index + 1}/${this.rowCount(cur)})`));
		return lines;
	}

	private confirm(): void {
		const cur = this.questions[this.step];
		let value: string;
		if (this.isOnCustomRow(cur)) {
			value = this.customInput.getValue().trim();
			if (!value) return; // can't submit an empty custom answer
		} else {
			value = cur.options[this.index].label;
		}
		this.answers[this.step] = value;
		if (this.step < this.questions.length - 1) {
			this.step += 1;
			this.index = 0;
			this.customInput.setValue("");
		} else {
			this.onSubmitCallback(this.answers.slice());
		}
	}

	private back(): void {
		if (this.step === 0) return;
		this.step -= 1;
		this.index = 0;
		this.customInput.setValue("");
	}

	handleInput(data: string): void {
		const kb = getKeybindings();
		const cur = this.questions[this.step];
		const rows = this.rowCount(cur);
		const onCustom = this.isOnCustomRow(cur);

		if (kb.matches(data, "tui.select.up")) {
			this.index = this.index === 0 ? rows - 1 : this.index - 1;
			return;
		}
		if (kb.matches(data, "tui.select.down")) {
			this.index = this.index === rows - 1 ? 0 : this.index + 1;
			return;
		}
		const arrows = this.customArrows();
		if (arrows.back && kb.matches(data, "app.options.back")) {
			this.back();
			return;
		}
		if ((arrows.next && kb.matches(data, "app.options.next")) || kb.matches(data, "tui.select.confirm")) {
			this.confirm();
			return;
		}
		if (kb.matches(data, "tui.select.cancel")) {
			this.onCancelCallback();
			return;
		}

		if (onCustom) {
			// Delegate free-form typing (insert, backspace, paste, word-delete)
			// to a hidden Input buffer; the value is rendered inline above.
			this.customInput.handleInput(data);
			return;
		}

		// Number quick-pick for the listed options (not the custom row).
		if (/^[1-9]$/.test(data)) {
			const n = Number(data) - 1;
			if (n < cur.options.length) {
				this.index = n;
				this.confirm();
			}
		}
	}
}
